// Tremor Button [v1.0.0]

import React from "react"
import { Slot } from "@radix-ui/react-slot"
import { RiLoader2Fill } from "@remixicon/react"
import { tv, type VariantProps } from "tailwind-variants"

import { cx, focusRing } from "@/lib/utils"

const buttonVariants = tv({
    base: [
        // base
        "relative inline-flex items-center",
        "justify-center whitespace-nowrap rounded-md",
        "border text-center font-medium transition-all shadow-xs transition-all duration-200 ease-in-out",
        "cursor-pointer",
        // disabled
        "disabled:pointer-events-none disabled:shadow-none",
        // focus
        focusRing,
    ],
    variants: {
        size: {
            xs: "px-2 py-1 text-xs",
            sm: "px-2 py-[5px] text-sm",
            base: "px-3 py-1.5 text-sm",
        },
        variant: {
            primary: [
                // border
                "border-transparent",
                // text color
                "text-white",
                // background color
                "bg-primary-blue",
                // hover color
                "hover:bg-[#228995] hover:text-white",
                // disabled
                "disabled:bg-[rgba(211,223,232,0.5)] disabled:text-[#8fb4c2]",
            ],
            secondary: [
                // border
                "border-[#d3e2e8]",
                // text color
                "text-[#2d3a3f]",
                // background color
                "bg-white",
                // hover color
                "hover:bg-[#74c4c6] hover:border-[#64a9ab] hover:text-[#2d3a3f] hover:shadow-[0_2px_8px_7px_rgba(34,137,149,0.2)]",
                // disabled
                "disabled:opacity-60",
            ],
            light: [
                // border
                "border-slate-200 dark:border-slate-200",
                // text color
                "text-gray-900",
                // background color
                "bg-white",
                //hover color
                "hover:bg-gray-50",
                // disabled
                "disabled:opacity-60",
            ],
            filter: [
                // border
                "border-[#D3DEE8]",
                // text color
                "text-[#313a3e]",
                // background color
                "bg-white",
                // hover color
                "hover:bg-[#313a3e] hover:text-white hover:border-[#313a3e]",
                // disabled
                "disabled:opacity-60",
            ],
            filterApplied: [
                // border
                "border-[#D3DEE8]",
                // text color
                "text-[#313a3e]",
                // background color
                "bg-white",
                // hover color
                "hover:bg-[#313a3e] hover:text-white hover:border-[#313a3e]",
                // disabled
                "disabled:opacity-60",
            ],
            ghost: [
                // base
                "shadow-none",
                // border
                "border-transparent",
                // text color
                "text-gray-900",
                // hover color
                "bg-transparent hover:bg-[rgba(255,255,255,0.1)]",
                // disabled
                "disabled:text-gray-400",
            ],
            destructive: [
                // text color
                "text-white",
                // border
                "border-transparent",
                // background color
                "bg-red-600",
                // hover color
                "hover:brightness-90",
                // disabled
                "disabled:bg-red-300 disabled:text-white",
            ],
            float: [
                "shadow-none",
                // border
                "border-transparent",
                // text color
                "text-[#2d3a3f]",
                // background color
                "bg-white",
                // hover color
                "hover:bg-gray-50",
                // disabled
                "disabled:opacity-60",
            ],
            outline: [
                // border
                "border-teal-600",
                // text color
                "text-teal-600",
                // background color
                "bg-white",
                // hover color
                "hover:bg-teal-600 hover:text-white",
                // disabled
                "disabled:bg-[rgba(211,223,232,0.5)] disabled:text-[#8fb4c2] disabled:border-[#c5d8e0]",
            ],
        },
    },
    defaultVariants: {
        variant: "primary",
        size: "base",
    },
})

interface ButtonProps
    extends React.ComponentPropsWithoutRef<"button">,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
    isLoading?: boolean
    loadingText?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            asChild,
            isLoading = false,
            loadingText,
            className,
            disabled,
            variant,
            size,
            children,
            ...props
        }: ButtonProps,
        forwardedRef,
    ) => {
        const Component = asChild ? Slot : "button"
        return (
            <Component
                ref={forwardedRef}
                className={cx(buttonVariants({ variant, size }), className)}
                disabled={disabled || isLoading}
                tremor-id="tremor-raw"
                {...props}
            >
                {isLoading ? (
                    <span className="pointer-events-none flex shrink-0 items-center justify-center gap-1.5">
                        <RiLoader2Fill
                            className="size-4 shrink-0 animate-spin"
                            aria-hidden="true"
                        />
                        <span className="sr-only">
                            {loadingText ? loadingText : "Loading"}
                        </span>
                        {loadingText ? loadingText : children}
                    </span>
                ) : (
                    children
                )}
            </Component>
        )
    },
)

Button.displayName = "Button"

export { Button, buttonVariants, type ButtonProps }